interface DecisionInfo {
    isPaymentMethodFIK: boolean;
    newInsurances: number;
    changedInsurances: number;
    coverableAmount: number;
    getHippoText: (resourceKey: string) => string;
}

interface HeaderConfig {
    title: string;
    bulletPoints: string[];
    buttonText: string;
}

const getDefaultConfig = ({
    isPaymentMethodFIK,
    coverableAmount,
    getHippoText
}: DecisionInfo): HeaderConfig => {
    let title: string = '';
    const bulletPoints: string[] = [];
    let buttonText: string = '';

    title = getHippoText('DSS-RECEIPT-PAGE-PURCHASE-HEADER');

    // Change the text to be plural depending on the amount of coverables
    if (coverableAmount === 1) {
        bulletPoints.push(getHippoText('DSS-RECEIPT-PAGE-SINGLE-COVERABLES-SUBHEADER'));
        buttonText = getHippoText('DSS-RECEIPT-PAGE-SINGLE-COVERABLE-BUTTON');
    } else {
        bulletPoints.push(getHippoText('DSS-RECEIPT-PAGE-MULT-COVERABLES-SUBHEADER'));
        buttonText = getHippoText('DSS-RECEIPT-PAGE-MULT-COVERABLE-BUTTON');
    }

    // Change text depending on payment method
    if (isPaymentMethodFIK) {
        bulletPoints.push(getHippoText('DSS-RECEIPT-PAGE-FIK-SUBHEADER'));
    } else {
        bulletPoints.push(getHippoText('DSS-RECEIPT-PAGE-BS-SUBHEADER'));
    }

    return { title, bulletPoints, buttonText };
};

const getBulletList = (textToSplit: string): string[] => {
    return textToSplit.split('-').filter((text) => text.length > 0);
};

const getNewCustomerBuysNewInsurancesConfig = ({
    isPaymentMethodFIK,
    coverableAmount,
    getHippoText
}: DecisionInfo): HeaderConfig => {
    let title: string = '';
    let bulletPoints: string[] = [];
    let buttonText: string = '';

    title = getHippoText('DSS-RECEIPT-PAGE-NEW-CUSTOMER-NEW-PURCHASE-HEADER');

    // Change the text to be plural depending on the amount of coverables
    if (coverableAmount === 1) {
        buttonText = getHippoText(
            'DSS-RECEIPT-PAGE-NEW-CUSTOMER-NEW-PURCHASE-SINGLE-COVERABLE-BUTTON'
        );
        const subHeader = getHippoText(
            'DSS-RECEIPT-PAGE-NEW-CUSTOMER-NEW-PURCHASE-SINGLE-COVERABLE-SUBHEADER'
        );
        bulletPoints = getBulletList(subHeader);
    } else {
        buttonText = getHippoText(
            'DSS-RECEIPT-PAGE-NEW-CUSTOMER-NEW-PURCHASE-MULT-COVERABLE-BUTTON'
        );
        const subHeader = getHippoText(
            'DSS-RECEIPT-PAGE-NEW-CUSTOMER-NEW-PURCHASE-MULT-COVERABLES-SUBHEADER'
        );
        bulletPoints = getBulletList(subHeader);
    }

    // Change text depending on payment method
    if (isPaymentMethodFIK) {
        bulletPoints.push(getHippoText('DSS-RECEIPT-PAGE-NEW-CUSTOMER-NEW-PURCHASE-FIK-SUBHEADER'));
    } else {
        bulletPoints.push(getHippoText('DSS-RECEIPT-PAGE-NEW-CUSTOMER-NEW-PURCHASE-BS-SUBHEADER'));
    }

    return { title, bulletPoints, buttonText };
};

const getExistingCustomerBuysNewInsurancesConfig = ({
    isPaymentMethodFIK,
    coverableAmount,
    getHippoText
}: DecisionInfo): HeaderConfig => {
    let title: string = '';
    let bulletPoints: string[] = [];
    let buttonText: string = '';

    title = getHippoText('DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-NEW-PURCHASE-HEADER');

    // Change the text to be plural depending on the amount of coverables
    if (coverableAmount === 1) {
        const subHeader = getHippoText(
            'DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-NEW-PURCHASE-SINGLE-COVERABLE-SUBHEADER'
        );
        buttonText = getHippoText(
            'DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-NEW-PURCHASE-SINGLE-COVERABLE-BUTTON'
        );
        bulletPoints = getBulletList(subHeader);
    } else {
        const subHeader = getHippoText(
            'DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-NEW-PURCHASE-MULT-COVERABLES-SUBHEADER'
        );
        buttonText = getHippoText(
            'DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-NEW-PURCHASE-MULT-COVERABLE-BUTTON'
        );
        bulletPoints = getBulletList(subHeader);
    }

    // Change text depending on payment method
    if (isPaymentMethodFIK) {
        bulletPoints.push(
            getHippoText('DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-NEW-PURCHASE-FIK-SUBHEADER')
        );
    } else {
        bulletPoints.push(
            getHippoText('DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-NEW-PURCHASE-BS-SUBHEADER')
        );
    }

    return { title, bulletPoints, buttonText };
};

const getExistingCustomerAcceptsChangesConfig = ({
    isPaymentMethodFIK,
    coverableAmount,
    getHippoText
}: DecisionInfo): HeaderConfig => {
    let title: string = '';
    let bulletPoints: string[] = [];
    let buttonText: string = '';

    title = getHippoText('DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-ACCEPT-CHANGES-HEADER');

    // Change the text to be plural depending on the amount of coverables
    if (coverableAmount === 1) {
        const subHeader = getHippoText(
            'DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-ACCEPT-CHANGES-SINGLE-COVERABLE-SUBHEADER'
        );
        buttonText = getHippoText(
            'DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-PURCHASE-AND-CHANGES-SINGLE-COVERABLE-BUTTON'
        );
        bulletPoints = getBulletList(subHeader);
    } else {
        const subHeader = getHippoText(
            'DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-ACCEPT-CHANGES-MULT-COVERABLES-SUBHEADER'
        );
        buttonText = getHippoText(
            'DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-ACCEPT-CHANGES-MULT-COVERABLE-BUTTON'
        );
        bulletPoints = getBulletList(subHeader);
    }

    // Change text depending on payment method
    if (isPaymentMethodFIK) {
        bulletPoints.push(
            getHippoText('DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-ACCEPT-CHANGES-FIK-SUBHEADER')
        );
    } else {
        bulletPoints.push(
            getHippoText('DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-ACCEPT-CHANGES-BS-SUBHEADER')
        );
    }

    return { title, bulletPoints, buttonText };
};

const getExistingCustomerBuysInsurancesAndAcceptsChangesConfig = ({
    isPaymentMethodFIK,
    newInsurances,
    changedInsurances,
    getHippoText
}: DecisionInfo): HeaderConfig => {
    let title: string = '';
    let bulletPoints: string[] = [];
    let buttonText: string = '';

    title = getHippoText('DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-PURCHASE-AND-CHANGES-HEADER');
    buttonText = getHippoText(
        'DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-PURCHASE-AND-CHANGES-MULT-COVERABLE-BUTTON'
    );

    // Change the text to be plural depending on the amount of added and changed coverables
    if (newInsurances === 1 && changedInsurances === 1) {
        const subHeader = getHippoText(
            'DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-PURCHASE-AND-CHANGES-SINGLE-COVERABLE-SUBHEADER'
        );
        bulletPoints = getBulletList(subHeader);
    } else {
        const subHeader = getHippoText(
            'DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-PURCHASE-AND-CHANGES-MULT-COVERABLES-SUBHEADER'
        );
        bulletPoints = getBulletList(subHeader);
    }

    // Change text depending on payment method
    if (isPaymentMethodFIK) {
        bulletPoints.push(
            getHippoText('DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-PURCHASE-AND-CHANGES-FIK-SUBHEADER')
        );
    } else {
        bulletPoints.push(
            getHippoText('DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-PURCHASE-AND-CHANGES-BS-SUBHEADER')
        );
    }

    return { title, bulletPoints, buttonText };
};

export const getHeaderConfig = (
    isPaymentMethodFIK: boolean,
    newInsurances: number,
    changedInsurances: number,
    isExistingCustomer: boolean | undefined,
    getHippoText: (resourceKey: string) => string
): HeaderConfig => {
    const decisionInfo: DecisionInfo = {
        isPaymentMethodFIK,
        getHippoText,
        newInsurances,
        changedInsurances,
        coverableAmount: newInsurances + changedInsurances
    };

    // New customer buys new insurance(s)
    if (!isExistingCustomer && !changedInsurances) {
        return getNewCustomerBuysNewInsurancesConfig(decisionInfo);
    }
    // Existing customer buys new insurance(s)
    else if (isExistingCustomer && !changedInsurances) {
        return getExistingCustomerBuysNewInsurancesConfig(decisionInfo);
    }
    // Existing customer accepts changes to one or more coverables
    else if (isExistingCustomer && changedInsurances && !newInsurances) {
        return getExistingCustomerAcceptsChangesConfig(decisionInfo);
    }
    // Existing customer buys insurance to one or more coverables and accepts changes to one or more coverables
    else if (isExistingCustomer && changedInsurances && newInsurances) {
        return getExistingCustomerBuysInsurancesAndAcceptsChangesConfig(decisionInfo);
    }

    return getDefaultConfig(decisionInfo);
};
